import { useEffect, useState } from 'react';
import { ApiError, api, type Topic, type VideoTemplate } from './api.js';

const LIBELLES_SOURCE: Record<Topic['source'], string> = {
  manual: 'Saisi à la main',
  watch: 'Veille',
  review: 'Avis client',
  listing: 'Bien à vendre',
};

export function Topics({ csrfToken }: { csrfToken: string | undefined }) {
  const [topics, setTopics] = useState<Topic[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [televersementEnCours, setTeleversementEnCours] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [formats, setFormats] = useState<VideoTemplate[]>([]);

  const familles = Array.from(new Map(formats.filter((template) => template.familySlug).map((template) => [template.familySlug as string, {
    slug: template.familySlug as string,
    name: template.familyName ?? template.name,
    formats: formats.filter((item) => item.familySlug === template.familySlug).length,
  }])).values());

  async function rafraichir() {
    try {
      const { topics: sujets } = await api.topics('ready');
      setTopics(sujets);
    } catch (error) {
      setErreur(error instanceof ApiError ? error.message : 'Chargement impossible.');
    }
  }

  useEffect(() => {
    void rafraichir();
    void api.videoTemplates().then((result) => setFormats(result.templates)).catch(() => setFormats([]));
  }, []);

  async function choisirFamille(topic: Topic, familySlug: string | null) {
    setEnCours(topic.id); setErreur(null); setMessage(null);
    try {
      await api.setTopicVideoTemplateFamily(topic.id, familySlug, csrfToken);
      setMessage(familySlug ? 'Gabarit multiformat sélectionné pour ce bien.' : 'Le gabarit « À vendre » sera utilisé.');
      await rafraichir();
    } catch (error) { setErreur(error instanceof ApiError ? error.message : 'Le format n’a pas pu être sélectionné.'); }
    finally { setEnCours(null); }
  }

  async function rediger(topic: Topic) {
    setEnCours(topic.id);
    setErreur(null);
    setMessage(null);
    try {
      const resultat = await api.generatePost(topic.id, csrfToken);
      const moteur = resultat.writer === 'anthropic' ? 'Claude' : 'Gemini';
      const repli = resultat.downgradedBecause ? ` — repli sur Gemini : ${resultat.downgradedBecause}` : '';
      setMessage(`« ${topic.title} » a été rédigé par ${moteur}${repli}. Il attend votre relecture.`);
      await rafraichir();
    } catch (error) {
      setErreur(error instanceof ApiError ? error.message : 'Rédaction impossible.');
    } finally {
      setEnCours(null);
    }
  }

  async function televerser(topic: Topic, file: File, role: 'source_video' | 'fallback_image') {
    setTeleversementEnCours(`${topic.id}:${role}`);
    setErreur(null);
    setMessage(null);
    try {
      await api.uploadTopicMedia(topic.id, file, role, csrfToken);
      setMessage(
        role === 'source_video'
          ? `La vidéo brute de « ${topic.title} » est prête à recevoir le gabarit.`
          : `La photo de repli de « ${topic.title} » est enregistrée.`,
      );
      await rafraichir();
    } catch (error) {
      setErreur(error instanceof ApiError ? error.message : 'Le téléversement a échoué.');
    } finally {
      setTeleversementEnCours(null);
    }
  }

  const total = topics?.length ?? 0;

  return (
    <section className="bloc sujets-page">
      <div className="intro-page">
        <div><p className="surtitre">Matière éditoriale</p><h2>{total || 'Aucun'} sujet{total > 1 ? 's' : ''} prêt{total > 1 ? 's' : ''}</h2></div>
        <p>Chaque proposition réunit un titre, une source et les faits nécessaires avant de demander au modèle de rédiger.</p>
      </div>

      {erreur ? <p className="erreur" role="alert">{erreur}</p> : null}
      {message ? <p className="succes" role="status">{message}</p> : null}
      {topics === null ? <p className="sous-titre">Chargement…</p> : null}
      {topics !== null && total === 0 ? <div className="vide"><span className="vide-icone">✦</span><p>Aucun sujet en attente.</p><span>Créez un sujet depuis le calendrier. La veille automatique viendra ensuite alimenter cette file.</span></div> : null}

      {topics !== null && total > 0 ? (
        <div className="liste-sujets">
          {topics.map((topic) => (
            <article key={topic.id} className="carte-sujet">
              <div className="source-sujet"><span className="source-icone">{topic.source === 'review' ? '★' : topic.source === 'listing' ? '⌂' : topic.source === 'watch' ? '↗' : '✎'}</span><div><span>{topic.category.name}</span><small>{LIBELLES_SOURCE[topic.source]} · {new Date(topic.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}</small></div><b>{topic.sourceVideoId || topic.fallbackImageId ? 'Médias prêts' : 'Prêt'}</b></div>
              <h3>{topic.title}</h3>
              <p className="etat-factuel">
                {topic.sourceVideoId
                  ? `Vidéo brute prête${topic.fallbackImageId ? ' · photo de repli prête' : ' · photo de repli conseillée'}`
                  : topic.fallbackImageId
                    ? 'Photo de repli prête · aucune vidéo brute'
                    : 'Le sujet utilisera le gabarit visuel de sa catégorie.'}
              </p>
              {topic.category.slug === 'biens-a-vendre' && familles.length > 0 ? <label className="choix-format-sujet">Gabarit de mise en vente
                <select value={topic.videoTemplateFamily?.slug ?? ''} disabled={enCours !== null} onChange={(event) => void choisirFamille(topic, event.target.value || null)}>
                  <option value="">À vendre · formats automatiques</option>
                  {familles.filter((family) => family.slug !== 'vente').map((family) => <option value={family.slug} key={family.slug}>{family.name} · {family.formats} format{family.formats > 1 ? 's' : ''}</option>)}
                </select>
              </label> : null}
              <div className="actions-sujet">
                <label className="action-secondaire">
                  <input type="file" accept="video/mp4,video/quicktime" disabled={televersementEnCours !== null} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void televerser(topic, file, 'source_video'); }} />
                  {televersementEnCours === `${topic.id}:source_video` ? 'Envoi…' : topic.sourceVideoId ? 'Remplacer la vidéo brute' : 'Ajouter la vidéo brute'}
                </label>
                <label className="action-secondaire">
                  <input type="file" accept="image/jpeg,image/png,image/webp" disabled={televersementEnCours !== null} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void televerser(topic, file, 'fallback_image'); }} />
                  {televersementEnCours === `${topic.id}:fallback_image` ? 'Envoi…' : topic.fallbackImageId ? 'Remplacer la photo de repli' : 'Ajouter une photo de repli'}
                </label>
                <button type="button" disabled={enCours !== null} onClick={() => void rediger(topic)}>{enCours === topic.id ? 'Rédaction en cours…' : 'Rédiger et composer'}</button>
              </div>
            </article>
          ))}
        </div>
      ) : null}

      {enCours ? <div className="travail-en-cours" role="status"><span /><p><strong>Le modèle prépare les versions réseau.</strong><small>La rédaction et la composition prennent généralement une trentaine de secondes.</small></p></div> : null}
    </section>
  );
}
